/**
 * ⚡ HIZLI ANALİZ — siteyi ciddi taramadan düşük maliyetli hızlı görünüm.
 * Kaynaklar: DB'deki sayfalar veya sitemap (tam tarama YOK) + GSC sayfa×sorgu (90g)
 * + GA4 iniş sayfaları (28g) + tek Haiku cluster çağrısı + 2 ucuz DataForSEO çağrısı.
 * Hedef: ~1-2 dakika, ~500 token.
 */
import { confirmCost } from "@/components/cost-confirm";
import { useState } from "react";
import { useLocation } from "wouter";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Zap, Loader2, Search, FileText, TrendingUp, MousePointerClick, Users, KeySquare, ExternalLink, AlertTriangle, Trophy, Sparkles, ScanSearch, Check } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from "recharts";

const C = { cyan: "#e83e3c", green: "#12b76a", red: "#f04438", amber: "#f79009", purple: "#e83e3c", blue: "#ec6461" };
const fmt = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(Math.round(n)));
const shortUrl = (u: string) => { try { const p = new URL(u); return p.pathname.replace(/\/$/, "") || "/"; } catch { return u; } };

type QuickPage = { url: string; title: string; clicks: number; impressions: number; position: number; topQuery: string; gaUsers: number };
type QuickCluster = { name: string; clicks: number; impressions: number; pageCount: number; pages: QuickPage[] };
type QuickAi = {
  gaAiUsers28d: number;
  byEngine: { engine: string; users: number }[];
  topAiPages: { path: string; users: number; engines: string[] }[];
  aioPlacementsTotal: number;
  aioTop: { question: string; aiSearchVolume: number; position: number; url: string }[];
  chatgptTests: { question: string; result: "cevapta" | "kaynakta" | "yok" }[];
};
type QuickData = {
  summary: {
    pagesFound: number; pagesUsed: number; inventorySource: "db" | "sitemap" | "gsc";
    gsc: boolean; ga: boolean; dfs: boolean; clusterCount: number;
    gscClicks90d: number; gscImpressions90d: number; gaUsers28d: number;
    organicKeywords: number; organicTraffic: number; cost: number; tookSec: number;
  };
  clusters: QuickCluster[];
  topQueries: { query: string; clicks: number; impressions: number; position: number }[];
  opportunities: { keyword: string; position: number; volume: number; url: string }[];
  ai?: QuickAi;
  gscTrend?: { date: string; clicks: number; impressions: number }[];
  topPages?: { path: string; clicks: number; impressions: number; position: number; topQuery: string; gaUsers: number }[];
  notes: string[];
};
type QuickResp = {
  latest: { runAt: string; data: QuickData } | null;
  running: boolean; runError: string | null;
  connected: { gsc: boolean; ga: boolean; dfs: boolean };
};
type MyProps = {
  connected: boolean; email?: string;
  gscSites: { siteUrl: string; url: string; added: boolean; domainId: number | null; gaPropertyId: string | null }[];
  gaProperties: { propertyId: string; displayName: string; account: string }[];
};
// Derin AI analizi (Trafik Analizi ile aynı veri — kompakt paneller)
type OvEngine = { question: string; aiSearchVolume: number; google: boolean; chatgpt: boolean; chatgptSource?: boolean; gemini: boolean; claude: boolean; perplexity: boolean };
type OvLatest = {
  runAt: string; share: number; placementsTotal: number;
  placements: { question: string; aiSearchVolume: number; url: string; position: number }[];
  engineMatrix: OvEngine[];
  topDomains: { domain: string; mentions: number; isUs: boolean }[];
  questions: { question: string; aiSearchVolume: number; cited: boolean }[];
};
type OvResp = { configured: boolean; latest: OvLatest | null; running?: boolean; runError?: string | null };
type VisLatest = { runAt: string; visibilityScore: number; sov: number; mentionRate: number; topBrands: { name: string; mentions: number; isUs: boolean }[] };
type VisResp = { configured: boolean; latest: VisLatest | null };

const MARKETS: { k: string; l: string }[] = [
  { k: "tr", l: "🇹🇷 TR" }, { k: "en", l: "🇺🇸 EN" }, { k: "de", l: "🇩🇪 DE" }, { k: "ar", l: "🇸🇦 AR" },
];

function StatCard({ icon: Icon, label, value, sub, color }: { icon: typeof Zap; label: string; value: string; sub?: string; color: string }) {
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Icon className="w-3.5 h-3.5" style={{ color }} /> {label}</div>
      <div className="text-2xl font-semibold tabular-nums" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </CardContent></Card>
  );
}

export default function QuickAnalysis() {
  const { data: domains } = useListDomains();
  const [activeDomainId, setActiveDomainId] = useActiveDomain(domains);
  const queryClient = useQueryClient();
  const [market, setMarket] = useState("tr");
  const [showAllQ, setShowAllQ] = useState(false);
  const [addingSite, setAddingSite] = useState<string | null>(null);

  // Google hesabındaki GSC/GA mülkleri — müşteri siteleri taramadan eklenip analiz edilir
  const { data: myProps } = useQuery({
    queryKey: ["my-properties"],
    queryFn: async (): Promise<MyProps> => {
      const r = await fetch("/api/google/my-properties");
      if (!r.ok) return { connected: false, gscSites: [], gaProperties: [] };
      return r.json();
    },
    staleTime: 5 * 60_000,
  });

  const addSite = useMutation({
    mutationFn: async (args: { gscSiteUrl: string; gaPropertyId?: string }): Promise<{ domainId: number }> => {
      const r = await fetch("/api/google/quick-domain", {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(args),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Site eklenemedi");
      return r.json();
    },
    onSuccess: (res) => {
      setActiveDomainId(res.domainId);
      queryClient.invalidateQueries(); // domain listesi + mülk listesi + analiz sorguları
      setAddingSite(null);
    },
    onError: () => setAddingSite(null),
  });

  const { data: qa } = useQuery({
    queryKey: ["quick-analysis", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<QuickResp> => {
      const r = await fetch(`/api/domains/${activeDomainId}/quick-analysis`);
      if (!r.ok) return { latest: null, running: false, runError: null, connected: { gsc: false, ga: false, dfs: false } };
      return r.json();
    },
    refetchInterval: (q) => (q.state.data?.running ? 4000 : false),
  });

  const run = useMutation({
    mutationFn: async (): Promise<{ started: boolean }> => {
      const r = await fetch(`/api/domains/${activeDomainId}/quick-analysis`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ market }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Hızlı analiz hatası");
      return r.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["quick-analysis", activeDomainId] }),
  });

  // Detaylı tarama: mevcut full-crawl pipeline'ı başlatır, domain detayına götürür
  const [, setLocation] = useLocation();
  const crawl = useMutation({
    mutationFn: async (): Promise<{ status: string }> => {
      const r = await fetch(`/api/domains/${activeDomainId}/crawl`, { method: "POST", headers: { "content-type": "application/json" }, body: "{}" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Tarama başlatılamadı");
      return r.json();
    },
    onSuccess: () => setLocation(`/domains/${activeDomainId}`),
  });

  // 🔬 Derin AI analizi (seçili domain) — Trafik Analizi ile aynı motorlar/veri
  const aiMarket = ["tr", "en", "ar"].includes(market) ? market : "auto";
  const { data: ov } = useQuery({
    queryKey: ["ai-overview", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<OvResp> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/ai-overview`); return r.ok ? await r.json() : { configured: false, latest: null }; } catch { return { configured: false, latest: null }; }
    },
    refetchInterval: (q) => (q.state.data?.running ? 5000 : false),
  });
  const runOv = useMutation({
    mutationFn: async (): Promise<{ started: boolean }> => {
      const r = await fetch(`/api/domains/${activeDomainId}/ai-overview`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ market: aiMarket }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "AI analizi hatası");
      return r.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ai-overview", activeDomainId] }),
  });
  const { data: vis } = useQuery({
    queryKey: ["ai-visibility", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<VisResp> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/ai-visibility`); return r.ok ? await r.json() : { configured: false, latest: null }; } catch { return { configured: false, latest: null }; }
    },
  });
  const runVis = useMutation({
    mutationFn: async (): Promise<unknown> => {
      const r = await fetch(`/api/domains/${activeDomainId}/ai-visibility`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ market: aiMarket }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "AI Visibility hatası");
      return r.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ai-visibility", activeDomainId] }),
  });

  const d = qa?.latest?.data ?? null;
  const s = d?.summary;
  const running = !!qa?.running || run.isPending;
  const srcLabel = s?.inventorySource === "db" ? "mevcut tarama" : s?.inventorySource === "sitemap" ? "sitemap (taramasız)" : "GSC sayfaları";
  const activeGsc = myProps?.gscSites.find((x) => x.domainId === activeDomainId);
  const ovRunning = !!ov?.running || runOv.isPending;

  return (
    <div className="px-4 sm:px-6 py-6 mx-auto w-full max-w-[1400px]">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Zap className="w-5 h-5" style={{ color: C.amber }} /> Hızlı Analiz</h1>
          <p className="text-sm text-muted-foreground mt-1">Siteyi <b>taramadan</b> hızlı görünüm: site haritası + arama verisi + ziyaretçi verisi + Geolop veri motoru. Cluster yapısı gerçek arama sorgularından çıkarılır. ~1 dk, ~500 token.</p>
        </div>
      </div>

      {/* Kontrol kartı — TEK domain seçilir; Google hesabındaki siteler de aynı seçicinin
          içinde "➕" ile listelenir (seçince otomatik eklenir, GSC+GA bağlanır). */}
      <Card className="mb-4"><CardContent className="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <Select value={String(activeDomainId ?? "")} onValueChange={(v) => {
              if (v.startsWith("gsc|")) { const site = v.slice(4); setAddingSite(site); addSite.mutate({ gscSiteUrl: site }); }
              else setActiveDomainId(Number(v));
            }}>
              <SelectTrigger className="w-[280px] h-8 text-xs"><SelectValue placeholder="Domain seç…" /></SelectTrigger>
              <SelectContent>
                {(domains ?? []).map((dm) => <SelectItem key={dm.id} value={String(dm.id)}>{dm.url.replace(/^https?:\/\//, "")}</SelectItem>)}
                {(() => {
                  // Aynı site hem https:// hem sc-domain: olarak listelenir — host'a göre tekilleştir
                  const hostOf = (u: string) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch { return u; } };
                  const seen = new Set((domains ?? []).map((dm) => hostOf(dm.url)));
                  return (myProps?.gscSites ?? [])
                    .filter((x) => !x.added)
                    .filter((x) => { const h = hostOf(x.url); if (seen.has(h)) return false; seen.add(h); return true; })
                    .map((x) => <SelectItem key={x.siteUrl} value={`gsc|${x.siteUrl}`}>➕ {x.url.replace(/^https?:\/\//, "")} (Google'dan ekle)</SelectItem>);
                })()}
              </SelectContent>
            </Select>
            {addSite.isPending && <span className="inline-flex items-center gap-1 text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> {addingSite?.replace(/^(sc-domain:|https?:\/\/)/, "")} ekleniyor, GSC+GA bağlanıyor…</span>}
            {([["Search Console", qa?.connected.gsc], ["GA4", qa?.connected.ga], ["DataForSEO", qa?.connected.dfs]] as const).map(([l, ok]) => (
              <span key={l} className="px-2 py-1 rounded-full" style={ok ? { background: `${C.green}22`, color: C.green } : { background: `${C.amber}22`, color: C.amber }}>{ok ? "✓" : "○"} {l}</span>
            ))}
            {activeGsc && !activeGsc.gaPropertyId && myProps && (
              // Bu domainin GA'sı otomatik eşleşmedi — buradan elle bağla
              <Select value="" onValueChange={(v) => addSite.mutate({ gscSiteUrl: activeGsc.siteUrl, gaPropertyId: v })}>
                <SelectTrigger className="h-8 w-[160px] text-[11px]" style={{ borderColor: C.amber, color: "#92400e" }}><SelectValue placeholder="⚠ GA4 bağla…" /></SelectTrigger>
                <SelectContent>{myProps.gaProperties.map((p) => <SelectItem key={p.propertyId} value={p.propertyId}>{p.displayName || p.propertyId}{p.account ? ` · ${p.account}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center rounded-lg border p-0.5" style={{ borderColor: "rgba(0,0,0,0.12)" }}>
              {MARKETS.map((o) => (
                <button key={o.k} onClick={() => setMarket(o.k)} title="Analiz pazarı" className="text-xs px-2 py-1 rounded-md transition" style={market === o.k ? { background: `${C.amber}33`, color: "#92400e" } : { color: "#70706c" }}>{o.l}</button>
              ))}
            </div>
            <Button size="sm" disabled={!activeDomainId || running} onClick={async () => { if (await confirmCost("quick-analysis")) run.mutate(); }} className="gap-1.5" style={{ background: C.amber, color: "#141412" }}>
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              {running ? "Analiz sürüyor… (~1-2 dk)" : d ? "Yeniden Analiz Et (~500 token)" : "Hızlı Analiz Et (~500 token)"}
            </Button>
            <Button size="sm" variant="outline" disabled={!activeDomainId || crawl.isPending} className="gap-1.5"
              onClick={async () => { if (await confirmCost("crawl", "Site komple taranır, cluster mimarisi ve link analizi çıkarılır. Mevcut tarama verisi yenilenir.")) crawl.mutate(); }}>
              {crawl.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5" />} Detaylı Tarat
            </Button>
          </div>
        </div>
        {run.isError && <p className="text-xs text-red-400 mt-2">{(run.error as Error)?.message}</p>}
        {qa?.runError && <p className="text-xs text-red-400 mt-2">{qa.runError}</p>}
        {addSite.isError && <p className="text-xs text-red-400 mt-2">{(addSite.error as Error)?.message}</p>}
        {crawl.isError && <p className="text-xs text-red-400 mt-2">{(crawl.error as Error)?.message}</p>}
      </CardContent></Card>

      {running && <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-6"><Loader2 className="w-4 h-4 animate-spin" /> Arka planda çalışıyor: envanter + GSC sorguları + GA4 + kümeleme + kelime profili… Sayfadan ayrılabilirsin.</div>}

      {!d && !running && (
        <div className="text-center text-sm text-muted-foreground py-16">
          Bu domain için henüz hızlı analiz yok. <b>Hızlı Analiz Et</b> ile başlat — site taranmaz, 1-2 dakikada özet çıkar.
        </div>
      )}

      {d && s && (<>
        {/* Özet kartları */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard icon={MousePointerClick} label="GSC Tıklama (90 gün)" value={s.gsc ? fmt(s.gscClicks90d) : "—"} sub={s.gsc ? `${fmt(s.gscImpressions90d)} gösterim` : "GSC bağlı değil"} color={C.blue} />
          <StatCard icon={Users} label="GA4 Kullanıcı (28 gün)" value={s.ga ? fmt(s.gaUsers28d) : "—"} sub={s.ga ? "iniş sayfası bazlı" : "GA4 bağlı değil"} color={C.purple} />
          <StatCard icon={KeySquare} label="Organik Kelime" value={s.dfs ? fmt(s.organicKeywords) : "—"} sub={s.dfs ? `~${fmt(s.organicTraffic)} tahmini trafik/ay` : "veri yok"} color={C.green} />
          <StatCard icon={FileText} label="Sayfa" value={fmt(s.pagesFound)} sub={`kaynak: ${srcLabel}`} color={C.amber} />
        </div>

        {/* GSC 90 günlük trend grafiği */}
        {!!d.gscTrend?.length && (
          <Card className="mb-4"><CardContent className="p-4">
            <div className="flex items-center gap-3 text-xs mb-2">
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.blue }} /> Tıklama</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.purple }} /> Gösterim</span>
              <span className="text-muted-foreground ml-auto">Son 90 gün (GSC)</span>
            </div>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.gscTrend.map((t) => ({ date: t.date.slice(5), Tıklama: t.clicks, Gösterim: t.impressions }))}>
                  <defs>
                    <linearGradient id="qaClicks" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.blue} stopOpacity={0.25} /><stop offset="100%" stopColor={C.blue} stopOpacity={0} /></linearGradient>
                    <linearGradient id="qaImpr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.purple} stopOpacity={0.2} /><stop offset="100%" stopColor={C.purple} stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={30} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={36} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={44} tickFormatter={(v: number) => fmt(v)} />
                  <RTooltip formatter={(v: number) => fmt(v)} labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Area yAxisId="l" type="monotone" dataKey="Tıklama" stroke={C.blue} strokeWidth={2} fill="url(#qaClicks)" />
                  <Area yAxisId="r" type="monotone" dataKey="Gösterim" stroke={C.purple} strokeWidth={2} fill="url(#qaImpr)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent></Card>
        )}

        {d.notes.length > 0 && (
          <Card className="mb-4" style={{ borderColor: `${C.amber}55` }}><CardContent className="p-3 text-xs" >
            {d.notes.map((n, i) => <div key={i} className="flex items-start gap-1.5" style={{ color: "#92400e" }}><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: C.amber }} /> {n}</div>)}
          </CardContent></Card>
        )}

        {/* Eski rapor (AI bölümü eklenmeden üretilmiş) — yeniden analiz iste */}
        {!d.ai && (
          <Card className="mb-5" style={{ borderColor: `${C.purple}66` }}><CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm">🤖 <b>AI analizi bu raporda yok</b> — bu rapor AI bölümü eklenmeden önce üretilmiş. Yeniden analiz edince <b>GA4 AI trafiği</b>, <b>AI Overview sıralamaları</b> ve <b>canlı ChatGPT testi</b> gelecek.</div>
            <Button size="sm" disabled={running} onClick={async () => { if (await confirmCost("quick-analysis")) run.mutate(); }} className="gap-1.5 shrink-0" style={{ background: C.purple, color: "#fff" }}>
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} AI ile Yeniden Analiz Et
            </Button>
          </CardContent></Card>
        )}

        {/* 🤖 AI ANALİZİ — kullanıcı için normal trafikten öncelikli */}
        {d.ai && (
          <Card className="mb-5" style={{ borderColor: `${C.purple}44` }}><CardContent className="p-4">
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-1.5">🤖 AI Analizi <span className="text-xs text-muted-foreground font-normal">— GA4 gerçek AI trafiği + Google AI Overview + canlı ChatGPT testi</span></h2>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div><div className="text-xs text-muted-foreground">AI Trafiği (28 gün)</div><div className="text-xl font-semibold tabular-nums" style={{ color: C.purple }}>{s.ga ? fmt(d.ai.gaAiUsers28d) : "—"}</div><div className="text-[10px] text-muted-foreground">{s.ga ? "GA4 gerçek kullanıcı" : "GA4 bağlı değil"}</div></div>
              <div><div className="text-xs text-muted-foreground">AI Overview Sıralaması</div><div className="text-xl font-semibold tabular-nums" style={{ color: C.cyan }}>{s.dfs ? fmt(d.ai.aioPlacementsTotal) : "—"}</div><div className="text-[10px] text-muted-foreground">soruda kaynak gösterildik</div></div>
              <div><div className="text-xs text-muted-foreground">ChatGPT Görünürlük</div><div className="text-xl font-semibold tabular-nums" style={{ color: C.green }}>{d.ai.chatgptTests.length ? `${d.ai.chatgptTests.filter((t) => t.result !== "yok").length}/${d.ai.chatgptTests.length}` : "—"}</div><div className="text-[10px] text-muted-foreground">test edilen top soruda</div></div>
            </div>

            {d.ai.byEngine.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 mb-3 text-xs">
                {d.ai.byEngine.map((e) => <span key={e.engine} className="px-2 py-0.5 rounded-full" style={{ background: `${C.purple}18`, color: C.purple }}>{e.engine}: {fmt(e.users)} kullanıcı</span>)}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-semibold mb-1.5">AI'ın Trafik Getirdiği Sayfalar (GA4)</h4>
                {d.ai.topAiPages.length === 0 ? <p className="text-[11px] text-muted-foreground">{s.ga ? "Son 28 günde AI kaynaklı ziyaret yok." : "GA4 bağlı değil — Google Hesabındaki Siteler kartından GA property seç."}</p> : (
                  <div className="space-y-1">{d.ai.topAiPages.map((p) => (
                    <div key={p.path} className="flex items-center justify-between gap-2 text-xs border-b last:border-0 pb-1">
                      <span className="truncate">{p.path}</span>
                      <span className="shrink-0 text-muted-foreground tabular-nums">{fmt(p.users)} · <span style={{ color: C.purple }}>{p.engines.join(", ")}</span></span>
                    </div>
                  ))}</div>
                )}
              </div>
              <div>
                <h4 className="text-xs font-semibold mb-1.5">AI Overview'de Sıralandığımız Sorular (top 10)</h4>
                {d.ai.aioTop.length === 0 ? <p className="text-[11px] text-muted-foreground">{s.dfs ? "Henüz hiçbir AI Overview'de kaynak gösterilmemişiz." : "DataForSEO tanımlı değil."}</p> : (
                  <div className="space-y-1">{d.ai.aioTop.map((q, i) => (
                    <a key={i} href={q.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-2 text-xs border-b last:border-0 pb-1 hover:bg-black/[0.03] rounded px-0.5 transition">
                      <span className="truncate">{q.question}</span>
                      <span className="shrink-0 text-muted-foreground tabular-nums">poz {q.position} · {fmt(q.aiSearchVolume)}</span>
                    </a>
                  ))}</div>
                )}
              </div>
            </div>

            {d.ai.chatgptTests.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold mb-1.5">Canlı ChatGPT Testi <span className="text-muted-foreground font-normal">(gerçek ChatGPT araması — cevap + Kaynaklar paneli)</span></h4>
                <div className="flex flex-wrap gap-1.5">{d.ai.chatgptTests.map((t, i) => (
                  <span key={i} className="text-[11px] px-2 py-1 rounded-md inline-flex items-center gap-1"
                    style={t.result === "cevapta" ? { background: `${C.green}18`, color: C.green } : t.result === "kaynakta" ? { background: `${C.amber}18`, color: "#92400e" } : { background: "rgba(0,0,0,0.04)", color: "#a3a3a0" }}>
                    {t.result === "cevapta" ? "✓ cevapta" : t.result === "kaynakta" ? "K kaynakta" : "· yok"} — {t.question}
                  </span>
                ))}</div>
                <p className="text-[10px] text-muted-foreground mt-1.5">Derin motor matrisi (tüm sorular × 4 AI motoru) için Trafik Analizi → Google AI Overview → Derin Analiz.</p>
              </div>
            )}
          </CardContent></Card>
        )}

        {/* En çok trafik alan sayfalar — grafik çubuklu liste */}
        {!!d.topPages?.length && (
          <Card className="mb-4"><CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><FileText className="w-4 h-4" style={{ color: C.blue }} /> En Çok Trafik Alan Sayfalar <span className="text-xs text-muted-foreground font-normal">(GSC 90 gün{s.ga ? " + GA4 kullanıcı" : ""})</span></h3>
            {(() => {
              const max = Math.max(...d.topPages!.map((p) => p.clicks), 1);
              return <div className="space-y-2">{d.topPages!.map((p) => (
                <div key={p.path}>
                  <div className="flex items-center justify-between gap-2 text-xs mb-0.5">
                    <span className="truncate">{p.path}{p.topQuery && <span className="ml-2 text-[10px]" style={{ color: C.cyan }}>„{p.topQuery}"</span>}</span>
                    <span className="shrink-0 text-muted-foreground tabular-nums">{fmt(p.clicks)} tık{p.gaUsers > 0 ? ` · ${fmt(p.gaUsers)} kull.` : ""}{p.position > 0 ? ` · poz ${p.position}` : ""}</span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: "rgba(0,0,0,0.06)" }}><div className="h-full rounded-full" style={{ width: `${(p.clicks / max) * 100}%`, background: `linear-gradient(90deg, ${C.blue}, ${C.cyan})` }} /></div>
                </div>
              ))}</div>;
            })()}
          </CardContent></Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Hızlı kazanım fırsatları */}
          <Card><CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><TrendingUp className="w-4 h-4" style={{ color: C.green }} /> Hızlı Kazanım Fırsatları <span className="text-xs text-muted-foreground font-normal">(pozisyon 4-20, hacme göre)</span></h3>
            {d.opportunities.length === 0 ? <p className="text-xs text-muted-foreground">{s.dfs ? "Bu site için kelime fırsatı bulunamadı — site yeni ya da kelime verisi henüz oluşmamış olabilir." : "Kelime fırsatları için DataForSEO anahtarı gerekli — Ayarlar ekranından tanımlanır."}</p> : (
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-background"><tr className="text-muted-foreground text-left border-b">
                    <th className="py-1.5">Kelime</th><th className="py-1.5 text-right">Poz</th><th className="py-1.5 text-right">Hacim</th><th className="py-1.5 pl-2">Sayfa</th>
                  </tr></thead>
                  <tbody>{d.opportunities.map((o, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1.5 pr-2">{o.keyword}</td>
                      <td className="py-1.5 text-right tabular-nums" style={{ color: o.position <= 10 ? C.green : C.amber }}>{o.position}</td>
                      <td className="py-1.5 text-right tabular-nums text-muted-foreground">{fmt(o.volume)}</td>
                      <td className="py-1.5 pl-2 truncate max-w-[160px]"><a href={o.url} target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-1" style={{ color: C.cyan }}>{shortUrl(o.url)}<ExternalLink className="w-3 h-3" /></a></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </CardContent></Card>

          {/* En çok tıklanan sorgular */}
          <Card><CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Search className="w-4 h-4" style={{ color: C.blue }} /> En Çok Tıklanan Sorgular <span className="text-xs text-muted-foreground font-normal">(GSC, 90 gün)</span></h3>
            {d.topQueries.length === 0 ? <p className="text-xs text-muted-foreground">{s.gsc ? "Search Console bu site için henüz sorgu verisi göndermedi — yeni bağlantılarda 1-2 gün sürebilir." : "Arama sorguları için Google Search Console bağlantısı gerekli — Ayarlar ekranından bağlanır."}</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-muted-foreground text-left border-b">
                    <th className="py-1.5">Sorgu</th><th className="py-1.5 text-right">Tık</th><th className="py-1.5 text-right">Gösterim</th><th className="py-1.5 text-right">Poz</th>
                  </tr></thead>
                  <tbody>{(showAllQ ? d.topQueries : d.topQueries.slice(0, 12)).map((q, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1.5 pr-2">{q.query}</td>
                      <td className="py-1.5 text-right tabular-nums">{fmt(q.clicks)}</td>
                      <td className="py-1.5 text-right tabular-nums text-muted-foreground">{fmt(q.impressions)}</td>
                      <td className="py-1.5 text-right tabular-nums text-muted-foreground">{q.position}</td>
                    </tr>
                  ))}</tbody>
                </table>
                {d.topQueries.length > 12 && <button onClick={() => setShowAllQ(!showAllQ)} className="text-[11px] mt-2" style={{ color: C.blue }}>{showAllQ ? "Daralt" : `+${d.topQueries.length - 12} sorgu daha`}</button>}
              </div>
            )}
          </CardContent></Card>
        </div>

        <p className="text-[11px] text-muted-foreground mt-4">
          Son analiz: {qa?.latest ? new Date(qa.latest.runAt).toLocaleString("tr-TR") : "—"} · süre {s.tookSec} sn · kullanılan token ~{Math.round(s.cost * 1000).toLocaleString("tr-TR")} · envanter: {srcLabel} ({s.pagesFound} URL bulundu, {s.pagesUsed} kullanıldı).
          Bu bir ilk-bakış raporudur — derin cluster mimarisi ve link analizi için <b>Detaylı Tarat</b>.
        </p>
      </>)}

      {/* ═══ 🔬 DERİN AI ANALİZİ — seçili domain (6 bölüm) ═══ */}
      {!!activeDomainId && (
        <div className="mt-8">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div>
              <h2 className="text-base font-semibold flex items-center gap-1.5"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /> Derin AI Analizi</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Sıralandığımız yerler · motor matrisi · hâkim domainler · gerçek AI soruları · visibility score · Share of Voice — yalnızca seçili domain için.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" disabled={ovRunning} onClick={async () => { if (await confirmCost("ai-overview")) runOv.mutate(); }} className="gap-1.5" style={{ background: C.cyan, color: "#141412" }}>
                {ovRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                {ovRunning ? "AI Overview analizi sürüyor… (~4 dk)" : "AI Overview Analizi (~500 token)"}
              </Button>
              <Button size="sm" disabled={runVis.isPending} onClick={async () => { if (await confirmCost("ai-visibility")) runVis.mutate(); }} className="gap-1.5" style={{ background: C.purple, color: "#fff" }}>
                {runVis.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {runVis.isPending ? "Visibility sürüyor…" : "AI Görünürlük Analizi (~750 token)"}
              </Button>
            </div>
          </div>
          {runOv.isError && <p className="text-xs text-red-400 mb-2">{(runOv.error as Error)?.message}</p>}
          {ov?.runError && <p className="text-xs text-red-400 mb-2">{ov.runError}</p>}
          {runVis.isError && <p className="text-xs text-red-400 mb-2">{(runVis.error as Error)?.message}</p>}
          {ovRunning && <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sıralandığımız tüm sorular 4 AI motorunda canlı test ediliyor — bitince otomatik yenilenir.</div>}
          {!ov?.latest && !ovRunning && <p className="text-xs text-muted-foreground mb-3">Bu domain için henüz derin AI analizi çalıştırılmadı — <b>AI Overview Analizi</b> ile başlat (taranmamış sitelerde konular GSC sorgularından türetilir).</p>}

          {ov?.latest && (<>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {/* 1. Sıralandığımız Yerler */}
              <Card><CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Trophy className="w-4 h-4" style={{ color: C.green }} /> AI Overview'lerde Sıralandığımız Yerler ({ov.latest.placementsTotal})</h3>
                {ov.latest.placements.length === 0 ? <p className="text-xs text-muted-foreground">Henüz hiçbir AI Overview'de kaynak gösterilmemişiz.</p> : (
                  <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">{[...ov.latest.placements].sort((a, b) => b.aiSearchVolume - a.aiSearchVolume).slice(0, 30).map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-2 text-xs border-b last:border-0 pb-1 hover:bg-black/[0.03] rounded px-0.5 transition">
                      <span className="truncate">{p.question}</span>
                      <span className="shrink-0 text-muted-foreground tabular-nums">poz {p.position} · {fmt(p.aiSearchVolume)}</span>
                    </a>
                  ))}</div>
                )}
              </CardContent></Card>

              {/* 2. AI Motorlarında Görünürlük */}
              <Card><CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /> AI Motorlarında Görünürlük</h3>
                {ov.latest.engineMatrix.length === 0 ? <p className="text-xs text-muted-foreground">Motor testi verisi yok.</p> : (() => {
                  const mScore = (m: OvEngine) => [m.chatgpt, m.gemini, m.claude, m.perplexity].filter(Boolean).length * 2 + (!m.chatgpt && m.chatgptSource ? 1 : 0);
                  const rows = [...ov.latest!.engineMatrix].sort((a, b) => (mScore(b) - mScore(a)) || b.aiSearchVolume - a.aiSearchVolume);
                  return (
                    <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
                      <table className="w-full text-xs min-w-[440px]">
                        <thead className="sticky top-0 bg-background"><tr className="text-muted-foreground text-left border-b">
                          <th className="py-1.5">Soru</th>{["AIO", "GPT", "Gem", "Cld", "Pplx"].map((h) => <th key={h} className="py-1.5 px-1.5 text-center">{h}</th>)}
                        </tr></thead>
                        <tbody>{rows.map((m, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-1.5 pr-2 truncate max-w-[220px]">{m.question}</td>
                            <td className="py-1.5 px-1.5 text-center"><Check className="w-3.5 h-3.5 inline" style={{ color: C.green }} /></td>
                            <td className="py-1.5 px-1.5 text-center">{m.chatgpt ? <Check className="w-3.5 h-3.5 inline" style={{ color: C.green }} /> : m.chatgptSource ? <span className="text-[10px] font-bold px-1 rounded" style={{ background: `${C.amber}22`, color: C.amber }}>K</span> : <span className="text-muted-foreground">·</span>}</td>
                            {[m.gemini, m.claude, m.perplexity].map((v, j) => <td key={j} className="py-1.5 px-1.5 text-center">{v ? <Check className="w-3.5 h-3.5 inline" style={{ color: C.green }} /> : <span className="text-muted-foreground">·</span>}</td>)}
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  );
                })()}
                <p className="text-[10px] text-muted-foreground mt-1.5">K = ChatGPT Kaynaklar panelinde varız (cevapta anılmadık).</p>
              </CardContent></Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {/* 3. Hâkim Domainler */}
              <Card><CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Trophy className="w-4 h-4" style={{ color: C.cyan }} /> AI Overview'da Hâkim Domainler</h3>
                {(() => {
                  const tds = ov.latest!.topDomains.slice(0, 10);
                  const max = Math.max(...tds.map((x) => x.mentions), 1);
                  return <div className="space-y-2">{tds.map((x, i) => (
                    <div key={x.domain}>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="truncate"><span className="text-muted-foreground">{i + 1}.</span> {x.domain}{x.isUs && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${C.green}22`, color: C.green }}>BİZ</span>}</span>
                        <span className="text-muted-foreground tabular-nums">{x.mentions}×</span>
                      </div>
                      <div className="h-1.5 rounded-full" style={{ background: "rgba(0,0,0,0.06)" }}><div className="h-full rounded-full" style={{ width: `${(x.mentions / max) * 100}%`, background: x.isUs ? C.green : C.cyan }} /></div>
                    </div>
                  ))}</div>;
                })()}
              </CardContent></Card>

              {/* 4. Gerçek AI Soruları */}
              <Card><CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Search className="w-4 h-4" style={{ color: C.blue }} /> Gerçek AI Soruları</h3>
                {ov.latest.questions.length === 0 ? <p className="text-xs text-muted-foreground">Soru verisi yok.</p> : (
                  <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">{ov.latest.questions.slice(0, 25).map((q, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs border-b last:border-0 pb-1">
                      <span className="truncate">{q.question}{q.cited && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${C.green}22`, color: C.green }}>✓ BİZ</span>}</span>
                      <span className="shrink-0 text-muted-foreground tabular-nums">{fmt(q.aiSearchVolume)}</span>
                    </div>
                  ))}</div>
                )}
              </CardContent></Card>
            </div>
          </>)}

          {vis?.latest && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* 5. AI Visibility Score */}
              <Card><CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4" style={{ color: C.purple }} /> AI Visibility Score</h3>
                <div className="flex items-end gap-6">
                  <div><div className="text-3xl font-semibold tabular-nums" style={{ color: C.purple }}>{vis.latest.visibilityScore}</div><div className="text-[11px] text-muted-foreground">görünürlük skoru</div></div>
                  <div><div className="text-3xl font-semibold tabular-nums" style={{ color: C.cyan }}>%{vis.latest.sov}</div><div className="text-[11px] text-muted-foreground">Share of Voice</div></div>
                  <div><div className="text-3xl font-semibold tabular-nums" style={{ color: C.green }}>%{Math.round(vis.latest.mentionRate)}</div><div className="text-[11px] text-muted-foreground">anılma oranı</div></div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">Son çalıştırma: {new Date(vis.latest.runAt).toLocaleString("tr-TR")} · GA4-eşlenik birleşik skor ve trend Trafik Analizi sayfasında.</p>
              </CardContent></Card>

              {/* 6. Share of Voice & Top Brands */}
              <Card><CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Trophy className="w-4 h-4" style={{ color: C.amber }} /> Share of Voice & Top Brands</h3>
                {(() => {
                  const tb = vis.latest!.topBrands.slice(0, 10);
                  const max = Math.max(...tb.map((b) => b.mentions), 1);
                  return <div className="space-y-2">{tb.map((b, i) => (
                    <div key={b.name}>
                      <div className="flex items-center justify-between text-xs mb-0.5">
                        <span className="truncate"><span className="text-muted-foreground">{i + 1}.</span> {b.name}{b.isUs && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${C.green}22`, color: C.green }}>BİZ</span>}</span>
                        <span className="text-muted-foreground tabular-nums">{b.mentions}×</span>
                      </div>
                      <div className="h-1.5 rounded-full" style={{ background: "rgba(0,0,0,0.06)" }}><div className="h-full rounded-full" style={{ width: `${(b.mentions / max) * 100}%`, background: b.isUs ? C.green : C.amber }} /></div>
                    </div>
                  ))}</div>;
                })()}
              </CardContent></Card>
            </div>
          )}
          {!vis?.latest && ov?.latest && <p className="text-xs text-muted-foreground mt-2">AI Visibility Score ile Share of Voice & Top Brands için <b>AI Visibility Analizi</b>'ni çalıştır.</p>}
        </div>
      )}
    </div>
  );
}
